import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import formatPrice from "../utils/formatPrice";
import { mediaUrl } from "../utils/mediaUrl";
import api from "../services/api";
import { getCheckoutConfig, checkDelivery } from "../services/orderService";
import { getMyProfile } from "../services/userService";
import "../css/cart.css";

import {
  getActiveCoupons,
  applyCoupon,
} from "../services/couponService";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

const PIN_STORAGE_KEY = "mithralaya.pincode";

const formatDeliveryDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
    : "";

const readSavedPincode = () => {
  try {
    return localStorage.getItem(PIN_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};


const CartPage = () => {
  const {
    items,
    updateCartItem,
    removeFromCart,
  } = useCart();

  const navigate = useNavigate();

  const [couponInput, setCouponInput] = React.useState("");
  const [coupons, setCoupons] = React.useState([]);
  const [appliedCoupon, setAppliedCoupon] = React.useState(null);
  const [discount, setDiscount] = React.useState(0);

  // Shipping/GST rules come from the server so this estimate matches checkout
  const [checkoutConfig, setCheckoutConfig] = React.useState({
    pricesIncludeGst: true,
    freeShippingAbove: 999,
    shippingFee: 49,
  });

  const [delivery, setDelivery] = React.useState(null);

  const cartQty = items.reduce((sum, item) => sum + (Number(item?.qty) || 0), 0);

  React.useEffect(() => {
    getCheckoutConfig()
      .then((cfg) => cfg && setCheckoutConfig(cfg))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const loadDelivery = async () => {
      let pin = readSavedPincode();
      // Guests have no profile to read a saved address from
      if (!/^[1-9][0-9]{5}$/.test(pin) && localStorage.getItem("mmw_token")) {
        try {
          const profile = await getMyProfile();
          const addr =
            (profile?.addresses || []).find((a) => a.isDefault) ||
            (profile?.addresses || [])[0];
          pin = String(addr?.pincode || "").trim();
        } catch {
          pin = "";
        }
      }
      if (!/^[1-9][0-9]{5}$/.test(pin)) {
        if (!cancelled) setDelivery(null);
        return;
      }
      try {
        const data = await checkDelivery(pin, { qty: Math.max(1, cartQty) });
        if (!cancelled) setDelivery({ ...data, pincode: pin });
      } catch {
        if (!cancelled) setDelivery(null);
      }
    };
    loadDelivery();
    return () => {
      cancelled = true;
    };
  }, [cartQty]);


  // =========================================================
  // LOAD COUPONS
  // =========================================================

  React.useEffect(() => {
    loadCoupons();
  }, []);


  const loadCoupons = async () => {
    try {
      const data = await getActiveCoupons();
      setCoupons(data);
    } catch (err) {
      console.log("Coupon loading error:", err);
    }
  };


  // =========================================================
  // CART ERROR MESSAGE - TAMIL
  // =========================================================


  const getCartErrorMessage = (error) => {
    const backendMessage =
      error?.response?.data?.message ||
      error?.message ||
      "";

    switch (backendMessage) {
      case "Selected size is not available":
        return "Selected size is not available.";

      case "Requested quantity is not available":
        return "Requested quantity is not available.";

      case "Selected color is not available":
        return "Selected color is not available.";

      case "Product not found":
        return "Product not found.";

      case "Item not found in cart":
        return "This product is not available in your cart.";

      case "Cart not found":
        return "Cart not found.";

      default:
        return "Unable to update cart. Please try again.";
    }
  };




  // =========================================================
  // APPLY COUPON
  // =========================================================

  const handleApplyCoupon = async (code) => {
    if (!code?.trim()) {
      toast.error("Please enter a coupon code.");
      return;
    }

    try {
      const res = await applyCoupon(code, itemsPrice);

      setAppliedCoupon({
        code,
        discount: res.discount,
      });

      setDiscount(res.discount);

      toast.success("Coupon Applied Successfully");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Invalid Coupon"
      );
    }
  };


  // =========================================================
  // CART PRICE
  // =========================================================

  const itemsPrice = items.reduce((sum, item) => {
    const price = Number(item?.product?.price) || 0;
    const qty = Number(item?.qty) || 0;

    return sum + price * qty;
  }, 0);


  // =========================================================
  // UPDATE QUANTITY
  // =========================================================

  const handleQtyChange = async (item, qty) => {
    if (qty < 1 || isNaN(qty)) {
      return;
    }

    try {
      await updateCartItem({
        productId: item.product._id,
        size: item.size,
        color: item.color,
        qty,
      });
    } catch (error) {
      console.error("Quantity update error:", error);

      toast.error(getCartErrorMessage(error));
    }
  };


  // =========================================================
  // UPDATE SIZE
  // =========================================================

  const handleSizeChange = async (item, size) => {
    try {
      await updateCartItem({
        productId: item.product._id,
        size: item.size,
        newSize: size,
        color: item.color,
        qty: item.qty,
      });
    } catch (error) {
      console.error("Size update error:", error);

      toast.error(getCartErrorMessage(error));
    }
  };


  // =========================================================
  // REMOVE COUPON
  // =========================================================

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
    setCouponInput("");

    toast.success("Coupon Removed Successfully");
  };


  // =========================================================
  // PLACE ORDER
  // =========================================================

  const handlePlaceOrder = () => {
    navigate("/checkout", {
      state: {
        coupon: appliedCoupon,
        discount,
        grandTotal,
      },
    });
  };


  // =========================================================
  // PRICE CALCULATION
  // =========================================================

  // Estimate only — the final total is calculated by the server at checkout.
  const shipping =
    itemsPrice === 0 || itemsPrice - discount >= checkoutConfig.freeShippingAbove
      ? 0
      : checkoutConfig.shippingFee;

  const gst = checkoutConfig.pricesIncludeGst ? 0 : Math.round((itemsPrice - discount) * 0.05);

  const grandTotal = itemsPrice + shipping + gst - discount;


  // =========================================================
  // UI
  // =========================================================

  return (
    <div className="cart-page">

      {/* =====================================================
          TOAST CONTAINER
      ====================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />


      {/* =====================================================
          EMPTY CART
      ====================================================== */}

      {items.length === 0 ? (

        <div className="empty-cart">

          <div className="empty-cart-inner">

            <svg className="empty-cart-image m-empty-bag" viewBox="0 0 120 120" width="120" height="120" fill="none" aria-hidden="true">
              <circle cx="60" cy="60" r="58" fill="#faf3f1" />
              <path d="M34 46h52l-4 44H38l-4-44z" stroke="#f4b6c4" strokeWidth="3" strokeLinejoin="round" />
              <path d="M48 46v-6a12 12 0 0124 0v6" stroke="#f4b6c4" strokeWidth="3" strokeLinecap="round" />
              <path d="M54 66c2 3 10 3 12 0" stroke="#d9466f" strokeWidth="3" strokeLinecap="round" />
            </svg>

            <div className="empty-cart-quote">

              <div className="empty-cart-quote-inner">

                <div className="empty-cart-quote-main">
                  Uh oh… your cart is empty!
                </div>

                <div className="empty-cart-quote-sub">
                  Add a few favourites to get started.
                </div>

              </div>

            </div>


            <button
              className="empty-cart-btn"
              onClick={() => navigate("/all-product")}
            >
              Continue Shopping
            </button>

          </div>

        </div>

      ) : (

        <>
          {/* =================================================
              CART TITLE
          ================================================== */}

          <h1 className="cart-title">
            Shopping Cart ({items.length})
          </h1>


          {/* =================================================
              PROGRESS
          ================================================== */}

          <div className="cart-progress">

            <div className="step active">

              <div className="circle">
                1
              </div>

              <p>
                Order Summary
              </p>

            </div>


            <div className="line"></div>


            <div className="step">

              <div className="circle">
                2
              </div>

              <p>
                Address
              </p>

            </div>


            <div className="line"></div>


            <div className="step">

              <div className="circle">
                3
              </div>

              <p>
                Payment
              </p>

            </div>

          </div>


          {/* =================================================
              CART LAYOUT
          ================================================== */}

          <div className="cart-layout">


            {/* =================================================
                LEFT SIDE
            ================================================== */}

            <div className="cart-left">


              {/* OFFER BAR */}

              <div className="offer-bar">

                <div className="offer-item">

                  <span className="offer-check">
                    ✓
                  </span>

                  <span>
                    Free Shipping
                  </span>

                </div>


                <div className="offer-item">

                  <span className="offer-check">
                    ✓
                  </span>

                  <span>
                    ₹250 off
                  </span>

                </div>


                <div className="offer-item">

                  <span className="offer-check">
                    ✓
                  </span>

                  <span>
                    ₹500 off
                  </span>

                </div>


                <div className="offer-item">

                  <span className="offer-check">
                    ✓
                  </span>

                  <span>
                    10% off Above ₹1499 (on 1st or...)
                  </span>

                </div>

              </div>


              {/* FREE DELIVERY */}

              <div className="free-delivery-line">

                <span className="truck-icon">
                  🚚
                </span>

                <span>
                  {delivery && delivery.serviceable === false
                    ? delivery.message || `We do not deliver to ${delivery.pincode} yet.`
                    : delivery?.deliveryBy
                      ? `${shipping === 0 ? "Free Delivery" : "Delivery"} by ${formatDeliveryDate(delivery.deliveryBy)}${delivery.estimated ? " (estimated)" : ""}${
                          delivery.shippingCharge != null
                            ? ` · Courier ₹${delivery.shippingCharge} (approx)`
                            : ""
                        }.`
                      : "Delivery date is confirmed at checkout after we check your pincode."}
                </span>

              </div>


              {/* =================================================
                  CART ITEMS
              ================================================== */}

              <div className="cart-items">

                {items.map((item, idx) => {

                  const price =
                    Number(item?.product?.price) || 0;

                  const qty =
                    Number(item?.qty) || 0;

                  const lineTotal =
                    price * qty;


                  return (

                    <div
                      key={idx}
                      className="cart-item"
                    >


                      {/* PRODUCT IMAGE */}

                      <Link
                        to={item?.product?.slug ? `/product/${item.product.slug}` : `/product/${item?.product?._id}${item?.productModel === "TextileProduct" ? "?textile=true" : ""}`}
                        className="cart-item-image-link"
                      >
                        {mediaUrl(item?.product?.images?.[0]) ? (
                          <img
                            src={mediaUrl(item?.product?.images?.[0])}
                            alt={
                              item?.product?.name ||
                              "Product"
                            }
                            className="cart-item-image"
                            onError={(event) => {
                              event.currentTarget.removeAttribute('src');
                            }}
                          />
                        ) : (
                          <span className="cart-item-image" aria-hidden="true" />
                        )}
                      </Link>


                      {/* PRODUCT INFO */}

                      <div className="cart-item-info">


                        {/* PRODUCT TOP */}

                        <div className="cart-item-top">

                          <Link
                            to={item?.product?.slug ? `/product/${item.product.slug}` : `/product/${item?.product?._id}${item?.productModel === "TextileProduct" ? "?textile=true" : ""}`}
                            className="cart-item-name"
                          >
                            {item?.product?.name || "Product"}
                          </Link>


                          <div className="cart-item-price">

                            {formatPrice(price)}

                          </div>

                        </div>


                        {/* SIZE + QUANTITY */}

                        <div className="cart-item-controls-row">


                          {/* SIZE */}

                          {(() => {
                            // The product's real sizes (bottoms: 28/30/32…, kurtis: S/M/L…)
                            const options = item?.product?.sizeOptions?.length
                              ? item.product.sizeOptions
                              : (item?.product?.sizes || []).map((s) => ({ size: s, stock: 1 }));
                            if (!options.length && !item.size) return null;
                            const list = options.some((o) => o.size === item.size)
                              ? options
                              : [{ size: item.size, stock: 1 }, ...options];
                            return (
                              <select
                                className="size-select"
                                aria-label="Size"
                                value={item.size || ""}
                                onChange={(e) => handleSizeChange(item, e.target.value)}
                              >
                                {list.map((o) => (
                                  <option key={o.size} value={o.size} disabled={o.stock <= 0 && o.size !== item.size}>
                                    Size: {o.size}{o.stock <= 0 && o.size !== item.size ? " (sold out)" : ""}
                                  </option>
                                ))}
                              </select>
                            );
                          })()}


                          {/* QUANTITY */}

                          <div className="qty-control">


                            <button
                              onClick={() =>
                                handleQtyChange(
                                  item,
                                  qty - 1
                                )
                              }
                            >
                              −
                            </button>


                            <input
                              type="number"
                              min="1"
                              value={qty}
                              onChange={(e) =>
                                handleQtyChange(
                                  item,
                                  Number(e.target.value)
                                )
                              }
                            />


                            <button
                              onClick={() =>
                                handleQtyChange(
                                  item,
                                  qty + 1
                                )
                              }
                            >
                              +
                            </button>


                          </div>

                        </div>


                        {/* REMOVE */}

                        <button
                          className="cart-remove-btn"
                          onClick={() =>
                            removeFromCart(
                              item.product._id,
                              item.size,
                              item.color
                            )
                          }
                        >
                          REMOVE
                        </button>


                        {/* NOTE */}

                        <div className="cart-item-note">

                          Sale items are not eligible
                          for additional discounts.

                        </div>


                      </div>

                    </div>

                  );

                })}

              </div>

            </div>


            {/* =================================================
                RIGHT SIDE
            ================================================== */}

            <div className="cart-right">


              {/* =================================================
                  COUPON BOX
              ================================================== */}

              <div className="coupon-box">


                <div className="coupon-box-header">

                  <h3>
                    Apply Coupon
                  </h3>

                  <Link
                    to="/coupons"
                    className="view-all-coupons-link"
                  >
                    View All
                  </Link>

                </div>


                {/* COUPON INPUT */}

                <div className="coupon-input-row">

                  <input
                    type="text"
                    placeholder="Enter Code"
                    value={couponInput}
                    onChange={(e) =>
                      setCouponInput(
                        e.target.value.toUpperCase()
                      )
                    }
                  />


                  <button
                    onClick={() =>
                      handleApplyCoupon(couponInput)
                    }
                  >
                    Apply
                  </button>

                </div>


                {/* COUPON STATUS */}

                <div className="coupon-status">

                  {appliedCoupon
                    ? `${appliedCoupon.code} Applied`
                    : "No Coupon Applied"}

                </div>


                <hr />


                {/* COUPON LIST */}

                <div className="coupon-list">

                  {coupons.map((c) => (

                    <div
                      className="coupon-list-item"
                      key={c.code}
                    >

                      <span className="coupon-check">
                        %
                      </span>


                      <div className="coupon-list-text">

                        <div className="coupon-code">
                          {c.code}
                        </div>

                        <div className="coupon-label">
                          {c.title}
                        </div>

                      </div>


                      <button
                        className="coupon-apply-btn"
                        onClick={() =>
                          handleApplyCoupon(c.code)
                        }
                      >
                        Apply
                      </button>

                    </div>

                  ))}

                </div>

              </div>


              {/* =================================================
                  ORDER SUMMARY
              ================================================== */}

              <div className="cart-summary">

                <h3>
                  Order Summary
                </h3>


                {/* SUBTOTAL */}

                <div className="summary-row">

                  <span>
                    Subtotal
                  </span>

                  <span>
                    {formatPrice(itemsPrice)}
                  </span>

                </div>


                {/* SHIPPING */}

                <div className="summary-row">

                  <span>
                    Shipping
                  </span>

                  <span>

                    {shipping === 0
                      ? "Free Delivery"
                      : formatPrice(shipping)}

                  </span>

                </div>

                {delivery?.serviceable && delivery.shippingCharge != null && (
                  <div className="summary-row muted">
                    <span>Courier estimate</span>
                    <span>{formatPrice(delivery.shippingCharge)} (approx)</span>
                  </div>
                )}


                {/* GST */}

                <div className="summary-row">

                  <span>
                    GST
                  </span>

                  <span>
                    {checkoutConfig.pricesIncludeGst
                      ? "Included in price"
                      : formatPrice(gst)}
                  </span>

                </div>


                {/* COUPON */}

                {appliedCoupon && (

                  <>

                    <div className="summary-row">

                      <span>
                        Coupon
                      </span>


                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >

                        <span
                          style={{
                            color: "green",
                            fontWeight: "600",
                          }}
                        >
                          {appliedCoupon.code}
                        </span>


                        <button
                          className="remove-coupon-btn"
                          onClick={handleRemoveCoupon}
                        >
                          Remove
                        </button>

                      </div>

                    </div>


                    <div className="summary-row">

                      <span>
                        Coupon Discount
                      </span>


                      <span
                        style={{
                          color: "green",
                          fontWeight: "600",
                        }}
                      >
                        -{formatPrice(discount)}
                      </span>

                    </div>

                  </>

                )}


                <hr />


                {/* TOTAL */}

                <div className="summary-row total">

                  <span>
                    Order Total
                  </span>

                  <span>
                    {formatPrice(grandTotal)}
                  </span>

                </div>


                {/* CHECKOUT */}

                <button
                  className="btn-secure-checkout"
                  onClick={handlePlaceOrder}
                >
                  🔒 Secure Checkout
                </button>

              </div>

            </div>

          </div>

        </>

      )}

    </div>
  );
};


export default CartPage;